// src/modules/pedidos/services/etiquetasPDF.js
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import logoUrualum from "../../../logo-urualum.png";
import { unidadesDeItem } from "./etiquetasFS";
import { getFlag, getEtiquetaFormato } from "./featureFlags";

// Logo cacheado como dataURL (se carga una sola vez)
let _logoDataUrl = null;
async function getLogoDataUrl() {
  if (_logoDataUrl) return _logoDataUrl;
  const res = await fetch(logoUrualum);
  const blob = await res.blob();
  _logoDataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  return _logoDataUrl;
}

const REMITENTE = { empresa: "NICUICO S.A.", direccion: "Isabela 3489", telefono: "Tel. 2216 9011" };

// ═══════════════════════════════════════════════════════════════════════════
//  FORMATO "DOBLE" — dos etiquetas chicas de 70×40 mm (cliente + Urualum)
// ═══════════════════════════════════════════════════════════════════════════
const D_W = 70, D_H = 40;

function d_conteoApilado(doc, unidad) {
  const xR = D_W - 5;
  doc.setTextColor(60); doc.setFont("helvetica", "normal"); doc.setFontSize(7);
  doc.text("Paq/Bultos", xR, 31, { align: "right" });
  doc.setTextColor(0); doc.setFont("helvetica", "bold"); doc.setFontSize(12);
  doc.text(`${unidad.indice}/${unidad.total}`, xR, 38, { align: "right" });
}

function d_nombreCliente(doc, nombre, x, ancho) {
  const texto = nombre || "—";
  doc.setFont("helvetica", "bold");
  let fs = 14; const min = 9;
  while (fs > min && doc.getStringUnitWidth(texto) * fs / doc.internal.scaleFactor > ancho) fs -= 1;
  doc.setFontSize(fs);
  if (doc.getStringUnitWidth(texto) * fs / doc.internal.scaleFactor <= ancho) { doc.text(texto, x, 11); return; }
  doc.text(doc.splitTextToSize(texto, ancho).slice(0, 2), x, 9);
}

function d_etiquetaCliente(doc, item, unidad) {
  const M = 5;
  d_nombreCliente(doc, item.cliente, M, D_W - M * 2);
  doc.setFont("helvetica", "normal"); doc.setFontSize(11);
  doc.text(item.direccion || "", M, 20);
  doc.text(item.localidad || "", M, 28);
  doc.setDrawColor(200); doc.line(M, 33, 42, 33);
  doc.setFontSize(8); doc.setTextColor(140);
  doc.text(`#${item.numero}`, M, 38);
  doc.setTextColor(0);
  d_conteoApilado(doc, unidad);
}

async function d_etiquetaUrualum(doc, item, unidad) {
  const M = 5;
  const logoSize = 14;
  try {
    const logoData = await getLogoDataUrl();
    doc.addImage(logoData, "PNG", M, M, logoSize, logoSize);
  } catch { /* sin logo */ }
  const textX = M + logoSize + 3;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(REMITENTE.empresa, textX, 10);
  doc.text(REMITENTE.direccion, textX, 16);
  doc.text(REMITENTE.telefono, textX, 22);
  doc.setDrawColor(200); doc.line(M, 33, 42, 33);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8);
  doc.text(`#${item.numero}`, M, 37);
  const qrData = await QRCode.toDataURL(unidad.key, { margin: 0 });
  const qrSize = 20;
  doc.addImage(qrData, "PNG", D_W - M - qrSize, 6, qrSize, qrSize);
  d_conteoApilado(doc, unidad);
}

async function generarDoble(items) {
  const doc = new jsPDF({ unit: "mm", format: [D_W, D_H], orientation: "landscape" });
  let primera = true;
  for (const item of items) {
    for (const unidad of unidadesDeItem(item)) {
      if (!primera) doc.addPage([D_W, D_H], "landscape");
      primera = false;
      d_etiquetaCliente(doc, item, unidad);
      doc.addPage([D_W, D_H], "landscape");
      await d_etiquetaUrualum(doc, item, unidad);
    }
  }
  return doc;
}

// ═══════════════════════════════════════════════════════════════════════════
//  FORMATO "UNICA" — una etiqueta 100×50 mm (courier, Opción A)
// ═══════════════════════════════════════════════════════════════════════════
const U_W = 100, U_H = 50;

function u_cliente(doc, nombre, x, y, ancho) {
  const texto = (nombre || "—").toUpperCase();
  doc.setFont("helvetica", "bold");
  let fs = 20; const min = 12;
  const cabe = (s) => doc.getStringUnitWidth(texto) * s / doc.internal.scaleFactor <= ancho;
  while (fs > min && !cabe(fs)) fs -= 1;
  if (cabe(fs)) { doc.setFontSize(fs); doc.text(texto, x, y); return; }
  doc.setFontSize(Math.min(fs, 15));
  doc.text(doc.splitTextToSize(texto, ancho).slice(0, 2), x, y - 3.5);
}

async function u_etiqueta(doc, item, unidad) {
  const W = U_W, M = 5;
  try {
    const logoData = await getLogoDataUrl();
    doc.addImage(logoData, "PNG", M, 2.8, 11, 11);
    doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(0);
    doc.text("URUALUM", M + 13, 10.5);
  } catch {
    doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(0);
    doc.text("URUALUM", M, 10.5);
  }
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(70);
  doc.text(`${REMITENTE.empresa} · ${REMITENTE.direccion}`, W - M, 7.5, { align: "right" });
  doc.text(REMITENTE.telefono, W - M, 12, { align: "right" });
  doc.setTextColor(0);
  doc.setDrawColor(170); doc.setLineWidth(0.3);
  doc.line(M, 16, W - M, 16);

  const qrSize = 22, qrX = W - M - qrSize, qrY = 19;
  const qrData = await QRCode.toDataURL(unidad.key, { margin: 0 });
  doc.addImage(qrData, "PNG", qrX, qrY, qrSize, qrSize);
  const cx = qrX + qrSize / 2;
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(90);
  doc.text("Paq/Bultos", cx, qrY + qrSize + 3.5, { align: "center" });
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(0);
  doc.text(`${unidad.indice}/${unidad.total}`, cx, qrY + qrSize + 8.5, { align: "center" });

  const leftW = qrX - M - 4;
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(120);
  doc.text("ENTREGAR A", M, 21);
  doc.setTextColor(140); doc.text(`#${item.numero}`, M + 22, 21);
  doc.setTextColor(0);
  u_cliente(doc, item.cliente, M, 29, leftW);
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(0);
  doc.text(doc.splitTextToSize(item.direccion || "", leftW).slice(0, 1), M, 36);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(60);
  doc.text(String(item.localidad || ""), M, 42);
  doc.setTextColor(0);
  const metodo = item.metodoEntrega || item.metodo;
  if (metodo) {
    const t = String(metodo).toUpperCase();
    doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    const tw = doc.getStringUnitWidth(t) * 9 / doc.internal.scaleFactor;
    doc.setDrawColor(0); doc.setLineWidth(0.4);
    doc.rect(M, 44.3, tw + 6, 5.4);
    doc.text(t, M + 3, 48);
  }
}

async function generarUnica(items) {
  const doc = new jsPDF({ unit: "mm", format: [U_W, U_H], orientation: "landscape" });
  let primera = true;
  for (const item of items) {
    for (const unidad of unidadesDeItem(item)) {
      if (!primera) doc.addPage([U_W, U_H], "landscape");
      primera = false;
      await u_etiqueta(doc, item, unidad);
    }
  }
  return doc;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Dispatcher — elige el formato según el flag (config/app.etiquetaFormato)
// ═══════════════════════════════════════════════════════════════════════════
/**
 * @param {Array<{pedidoId, numero, cliente, direccion, localidad, bultos, paquetes, metodoEntrega?}>} items
 * @param {{ formato?: "doble"|"unica" }} [opts]
 * @returns {Promise<jsPDF>}
 */
export async function generarEtiquetasPDF(items, { formato } = {}) {
  const fmt = formato || await getEtiquetaFormato();
  return fmt === "unica" ? generarUnica(items) : generarDoble(items);
}

export async function generarEImprimirEtiquetas(items) {
  if (await getFlag("ETIQUETAS_OFF")) {
    console.log("[etiquetas] impresión desactivada por flag ETIQUETAS_OFF — no se imprime");
    return;
  }
  const doc = await generarEtiquetasPDF(items);
  doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank");
}
